// The `data-ui-directive` frame the gateway writes into the `POST /api/chat`
// UI-message stream when a worker calls `present_decision`. A decision turn is
// carried ENTIRELY by this frame — the gateway emits no text for it — so a
// client that drops the frame renders an empty reply.
//
// This module is the wire contract only: the shapes a client can rely on, plus
// a tolerant reader that narrows an untrusted parsed frame. The gateway keeps
// its own stricter emission-side validation (clamping lengths, rejecting
// placeholder calls); those are normalisation concerns a reader must not
// re-litigate. What is shared — and must not drift — are the type declarations
// below, which `apps/web/lib/ui-directives.ts` re-exports.

/** The wire `type` of a UI-directive stream part. */
export const UI_DIRECTIVE_PART_TYPE = "data-ui-directive" as const;

/** Which modality raised the directive. */
export type UiDirectiveSource = "voice" | "text";

export type UiDirectiveState =
  | { status: "pending" }
  | { status: "submitting"; optionIndex: number }
  | { status: "selected"; optionIndex: number; acknowledgement: "pending" | "complete" | "error" }
  | { status: "dismissed" }
  | { status: "error"; message: string };

export interface UiDirectiveV1<Name extends string = string, Args = unknown> {
  version: 1;
  name: Name;
  callId: string;
  args: Args;
  state: UiDirectiveState;
}

export interface UiDirectiveResultV1<Name extends string = string, Result = unknown> {
  version: 1;
  name: Name;
  callId: string;
  result: Result;
}

export interface PresentDecisionArgs {
  title: string;
  options: { label: string; detail: string }[];
}

export type PresentDecisionDirectiveV1 = UiDirectiveV1<"present_decision", PresentDecisionArgs>;
export type PresentDecisionResultV1 = UiDirectiveResultV1<
  "present_decision",
  { status: "selected"; optionIndex: number }
>;

/** The `data` payload of a `data-ui-directive` stream part. */
export interface UiDirectiveFrameV1 {
  directive: PresentDecisionDirectiveV1;
  source: UiDirectiveSource;
}

const MIN_OPTIONS = 2;
const MAX_OPTIONS = 4;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const nonEmpty = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

const inRange = (value: unknown, count: number): value is number =>
  typeof value === "number" && Number.isInteger(value) && value >= 0 && value < count;

function parseArgs(input: unknown): PresentDecisionArgs | null {
  if (!isRecord(input) || !nonEmpty(input.title) || !Array.isArray(input.options)) return null;
  const { options } = input;
  if (options.length < MIN_OPTIONS || options.length > MAX_OPTIONS) return null;
  const parsed: PresentDecisionArgs["options"] = [];
  for (const option of options) {
    if (!isRecord(option) || !nonEmpty(option.label) || !nonEmpty(option.detail)) return null;
    parsed.push({ label: option.label, detail: option.detail });
  }
  return { title: input.title, options: parsed };
}

function parseState(input: unknown, optionCount: number): UiDirectiveState | null {
  if (!isRecord(input)) return null;
  switch (input.status) {
    case "pending":
      return { status: "pending" };
    case "dismissed":
      return { status: "dismissed" };
    case "submitting":
      return inRange(input.optionIndex, optionCount)
        ? { status: "submitting", optionIndex: input.optionIndex }
        : null;
    case "selected":
      if (!inRange(input.optionIndex, optionCount)) return null;
      return input.acknowledgement === "pending" ||
        input.acknowledgement === "complete" ||
        input.acknowledgement === "error"
        ? { status: "selected", optionIndex: input.optionIndex, acknowledgement: input.acknowledgement }
        : null;
    case "error":
      return nonEmpty(input.message) ? { status: "error", message: input.message } : null;
    default:
      return null;
  }
}

/**
 * Narrow the `data` payload of a `data-ui-directive` frame, or return `null` if
 * it is not one this contract version understands.
 *
 * Deliberately tolerant of UNKNOWN extra fields (a newer gateway may add them)
 * but strict about every field a consumer reads — so a returned frame is always
 * safe to render. Returns a fresh object; never aliases the input.
 */
export function parseUiDirectiveFrame(input: unknown): UiDirectiveFrameV1 | null {
  if (!isRecord(input)) return null;
  if (input.source !== "voice" && input.source !== "text") return null;
  const directive = input.directive;
  if (!isRecord(directive)) return null;
  if (directive.version !== 1 || directive.name !== "present_decision") return null;
  if (!nonEmpty(directive.callId)) return null;
  const args = parseArgs(directive.args);
  if (!args) return null;
  const state = parseState(directive.state, args.options.length);
  if (!state) return null;
  return {
    directive: { version: 1, name: "present_decision", callId: directive.callId, args, state },
    source: input.source,
  };
}
